import React, { useState, useEffect, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Card, CardContent } from "../components/ui/card";
import ChatWidget from "../components/ChatWidget";
import SeasonalDecorations from "../components/SeasonalDecorations";
import LatestBlogPosts from "../components/LatestBlogPosts";
import { useToast } from "../hooks/use-toast";
import { useSEO, SEO_PRESETS } from "../hooks/useSEO";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CheckCircle, Shield, Clock, MapPin, Phone, Mail, Laptop2, Users, ArrowRight, Star, Zap, Gift, Heart, Sparkles } from "lucide-react";
import { postLead } from "../lib/api";

interface SectionProps {
  id?: string;
  className?: string;
  children: ReactNode;
}

export const Section = ({ id, className = "", children }: SectionProps) => (
  <section id={id} className={`relative py-16 md:py-24 overflow-hidden ${className}`}>
    <div className="mx-auto w-full max-w-7xl px-6">{children}</div>
  </section>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const nav = [
    { label: "Warum", href: "#warum" },
    { label: "Leistungen", href: "#leistungen" },
    { label: "Zeiten", href: "#zeiten" },
    { label: "Preise", href: "#preise" },
    { label: "FAQ", href: "#faq" },
    { label: "Blog", href: "/blog", internal: true },
    { label: "Kompakt", href: "/kompakt", internal: true },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenuOpen(false);
  };
  
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm">
      {/* Top Bar with Contact Info - Mobile & Desktop */}
      <div className="bg-slate-800 text-white py-2">
        <div className="mx-auto max-w-7xl px-4 md:px-6 flex flex-wrap items-center justify-center md:justify-between gap-2 md:gap-4 text-sm md:text-base">
          <a 
            href="tel:+4915128958006" 
            className="flex items-center gap-2 hover:text-[--brand-accent] transition-colors font-semibold"
          >
            <Phone className="h-4 w-4" />
            <span>01512 895 800 6</span>
          </a>
          <a 
            href="https://wa.me/4915128958006?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zum%20IT-Support."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-1.5 rounded-lg transition-colors font-semibold"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
      
      {/* Main Navigation */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png" 
            alt="Klartech Müritz Logo" 
            className="h-10 md:h-12 w-auto object-contain"
          />
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 lg:flex">
          {nav.map((n, index) => (
            n.internal ? (
              <Link
                key={n.href}
                to={n.href}
                className="text-base font-semibold text-slate-700 transition-colors hover:text-[--brand-primary]"
              >
                {n.label}
              </Link>
            ) : (
              <button
                key={n.href + '-' + index}
                onClick={() => scrollTo(n.href)}
                className="text-base font-semibold text-slate-700 transition-colors hover:text-[--brand-primary]"
              >
                {n.label}
              </button>
            )
          ))}
        </nav>
        
        <div className="flex items-center gap-3">
          {/* Gift Voucher Button - Desktop */}
          <Link to="/gutschein">
            <Button
              variant="outline"
              className="hidden md:flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-2.5 text-red-700 font-semibold hover:bg-red-100 hover:border-red-300 transition-all"
            >
              <Gift className="h-4 w-4" />
              Gutschein
            </Button>
          </Link>
          
          {/* Desktop CTA */}
          <Button
            onClick={() => scrollTo("#kontakt")}
            className="hidden md:flex rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-dark] px-6 py-3 text-white shadow-lg transition-all text-base font-bold"
          >
            Kontakt aufnehmen
          </Button>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? (
              <svg className="h-7 w-7 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-7 w-7 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg">
          <nav className="flex flex-col px-4 py-4 gap-2">
            {nav.map((n, index) => (
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-semibold text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {n.label}
                </Link>
              ) : (
                <button
                  key={n.href + '-' + index}
                  onClick={() => scrollTo(n.href)}
                  className="text-left text-lg font-semibold text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {n.label}
                </button>
              )
            ))}
            <Link
              to="/gutschein"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 text-lg font-semibold text-red-700 py-3 px-4 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
            >
              <Gift className="h-5 w-5" />
              Gutschein verschenken
            </Link>
            <Button
              onClick={() => scrollTo("#kontakt")}
              className="mt-2 w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-dark] px-6 py-4 text-white shadow-lg transition-all text-lg font-bold"
            >
              Kontakt aufnehmen
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

const Hero = () => {
  return (
    <Section id="home" className="py-12 md:py-16 bg-gradient-to-b from-slate-50 to-white">
      <div className="grid items-center gap-10 lg:gap-16 lg:grid-cols-2">
        
        {/* Left Content - Clean & Senior-Friendly */}
        <div className="space-y-8">
          
          {/* Location Badge */}
          <div className="inline-flex items-center gap-3 rounded-full bg-green-50 px-5 py-3 text-base font-semibold text-slate-700 border-2 border-green-200">
            <div className="h-3 w-3 rounded-full bg-green-500"></div>
            <MapPin className="h-5 w-5 text-[--brand-primary]" />
            <span>IT-Support in Waren (Müritz)</span>
          </div>
          
          {/* Main Headline - Larger, Clearer */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-tight mb-6">
              Technik-Probleme?
              <span className="block text-[--brand-primary] mt-2">
                Wir helfen Ihnen.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl font-bold text-slate-700 mb-4">
              Computer • Smartphone • Tablet • Smart Home
            </p>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              Geduldige, verständliche IT-Hilfe für <strong className="text-slate-800">Senioren, Privatleute und Kleinunternehmen</strong>. 
              Keine Fachsprache. Keine versteckten Kosten.
            </p>
            
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl mt-4 bg-slate-50 border-l-4 border-[--brand-primary] pl-4 py-2 rounded-r-lg">
              Klartech Müritz bietet nicht nur Hilfe im Notfall, sondern auch <strong className="text-slate-800">laufende IT-Betreuung im praktischen Monatsabo</strong> – für Senioren, Privatpersonen und kleine Unternehmen.
            </p>
          </div>
          
          {/* CTA Buttons - Larger & Clearer */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[--brand-primary] hover:bg-[--brand-primary-dark] text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg transition-all"
            >
              Kostenloses Erstgespräch
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            
            <Button
              onClick={() => document.querySelector("#preise")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-white border-2 border-[--brand-primary] text-[--brand-primary] hover:bg-[--brand-primary] hover:text-white px-8 py-6 text-lg font-bold rounded-xl transition-all"
            >
              Zu den Betreuungspaketen
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </div>
          
          {/* Phone CTA */}
          <a
            href="tel:+4915128958006"
            className="inline-flex items-center justify-center gap-3 text-slate-600 hover:text-[--brand-primary] text-lg font-semibold transition-all"
          >
            <Phone className="h-5 w-5" />
            Oder rufen Sie an: 01512 895 800 6
          </a>
          
          {/* Trust Indicator - Simple */}
          <div className="flex items-center gap-4 bg-slate-50 rounded-xl px-6 py-4 border border-slate-200">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
              ))}
            </div>
            <div>
              <div className="font-bold text-slate-800 text-lg">Zufriedene Kunden</div>
              <div className="text-base text-slate-600">in Waren (Müritz) und Umgebung</div>
            </div>
          </div>
        </div>
        
        {/* Right Content - Clean Hero Image */}
        <div className="relative">
          <Card className="overflow-hidden rounded-2xl border-0 shadow-xl">
            <CardContent className="p-0 relative">
              <img 
                src="https://images.unsplash.com/photo-1618171889969-0feeb769fe78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxJVCUyMHN1cHBvcnQlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc1NjIzNjgzN3ww&ixlib=rb-4.1.0&q=85" 
                alt="IT-Support: Hilfe bei Computer, Smartphone und Tablet" 
                className="h-[350px] lg:h-[450px] w-full object-cover" 
              />
              
              {/* Info Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[--brand-primary] rounded-xl flex items-center justify-center">
                        <Laptop2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-lg">Klartech Müritz</div>
                        <div className="text-base text-slate-600">Ihr IT-Partner vor Ort</div>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg font-semibold">
                      <Clock className="h-5 w-5" />
                      Mo-Fr verfügbar
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
};

const Why = () => {
  const benefits = [
    { title: "Lokaler Service", desc: "Persönlicher Kontakt in Waren (Müritz) und Umgebung" },
    { title: "Verständlich erklärt", desc: "Keine Fachsprache - wir sprechen Ihre Sprache" },
    { title: "Geduldige Hilfe", desc: "Wir nehmen uns Zeit für Ihre Fragen" },
    { title: "Faire Preise", desc: "Transparente Kosten ohne versteckte Gebühren" },
    { title: "Flexibel verfügbar", desc: "Remote-Support oder Hausbesuch nach Wunsch" },
    { title: "Zuverlässig", desc: "Termine werden eingehalten, Versprechen gehalten" }
  ];
  
  const icons = [CheckCircle, Clock, Shield, MapPin, Users, Laptop2];
  
  return (
    <Section id="warum" className="bg-gradient-to-b from-white to-slate-50 relative">
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-px h-24 bg-gradient-to-b from-slate-300 to-transparent"></div>
      
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          Warum <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 bg-clip-text text-transparent">Klartech Müritz</span>?
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
          Ihr vertrauensvoller IT-Partner in Waren (Müritz) – geduldige, zuverlässige und verständliche Computer-Hilfe für alle Technik-Fragen.
        </p>
      </div>
      
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {benefits.map((b, i) => {
          const Icon = icons[i % icons.length];
          return (
            <div
              key={b.title}
              className="group relative rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:scale-105 hover:-translate-y-2"
            >
              <div className="absolute -top-6 left-8 z-10">
                <div className="inline-flex rounded-2xl bg-gradient-to-br from-[--brand-primary] to-blue-600 p-4 shadow-lg">
                  <Icon size={24} className="text-white" />
                </div>
              </div>
              <div className="pt-6 relative z-20">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const Services = () => {
  return (
    <Section id="leistungen" className="bg-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_70%)]"></div>
      
      <div className="relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Unsere <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 bg-clip-text text-transparent">Leistungen</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Maßgeschneiderte IT-Unterstützung für Ihren Bedarf
          </p>
        </div>

        <div className="grid items-start gap-16 lg:grid-cols-2">
          <div className="group">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 transition-all duration-500 hover:shadow-xl">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 mb-4">
                  <Laptop2 size={16} />
                  Für Kleinunternehmen & Freiberufler
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Business IT-Support</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Schneller Remote-Support bei PC-Problemen und professionelle IT-Beratung.
                </p>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Schneller Remote-Support bei PC-Problemen</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Unterstützung bei Bürosoftware (z.B. M365)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Beratung zu Datensicherung & IT-Sicherheit</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Grundlegende Website-Pflege (Updates etc.)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Technik-orientierte virtuelle Assistenz</span>
                </li>
              </ul>
              
              <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                <img 
                  src="https://images.pexels.com/photos/1181472/pexels-photo-1181472.jpeg" 
                  alt="Business IT Support" 
                  className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
          
          <div className="group">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 transition-all duration-500 hover:shadow-xl">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700 mb-4">
                  <Users size={16} />
                  Für Senioren & Privatpersonen
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Senior IT-Hilfe</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Geduldige, verständliche Unterstützung – Schritt für Schritt.
                </p>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Bedienung von Smartphone, Tablet, PC</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Sicheres Internet, E-Mail & Online-Banking</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Einrichtung von Videoanrufen</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Schutz vor Viren & Online-Betrug</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Hilfe bei Drucker, WLAN & Co.</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                  <span>Geduldige Schulungen & Erklärungen</span>
                </li>
              </ul>
              
              <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                <img 
                  src="https://images.pexels.com/photos/3768140/pexels-photo-3768140.jpeg" 
                  alt="Senior IT Hilfe" 
                  className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

// Weihnachts-Spezial: Neue Geräte einrichten
const ChristmasDeviceSetup = () => {
  return (
    <Section className="bg-gradient-to-br from-red-50 via-white to-green-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-red-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-green-100 rounded-full blur-3xl opacity-50"></div>
      
      <div className="relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-5 py-2 text-red-700 font-semibold mb-6">
            <span className="text-xl">🎄</span>
            Weihnachts-Spezial
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Neues Gerät zu <span className="text-red-600">Weihnachten</span>?
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Tablet, Smartphone oder Laptop geschenkt bekommen? Ich helfe bei der Einrichtung!
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          <Card className="p-6 bg-white/80 backdrop-blur border-2 border-red-100 hover:border-red-300 transition-all hover:shadow-lg">
            <div className="text-4xl mb-4">📱</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Smartphone & Tablet</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Grundeinrichtung & WLAN</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>WhatsApp & Video-Telefonie</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Kontakte & Fotos übertragen</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Wichtige Apps installieren</span>
              </li>
            </ul>
          </Card>

          <Card className="p-6 bg-white/80 backdrop-blur border-2 border-green-100 hover:border-green-300 transition-all hover:shadow-lg">
            <div className="text-4xl mb-4">💻</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Laptop & PC</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Windows einrichten</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>E-Mail & Browser konfigurieren</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Drucker & Scanner verbinden</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Datensicherung einrichten</span>
              </li>
            </ul>
          </Card>

          <Card className="p-6 bg-white/80 backdrop-blur border-2 border-blue-100 hover:border-blue-300 transition-all hover:shadow-lg">
            <div className="text-4xl mb-4">🏠</div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">Smart Home</h3>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Smart-TV einrichten</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Streaming-Dienste (Netflix & Co.)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>Alexa & Google Home</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                <span>WLAN-Optimierung</span>
              </li>
            </ul>
          </Card>
        </div>

        <div className="mt-10 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg"
            >
              🎁 Termin vereinbaren
            </Button>
            <a href="/gutschein">
              <Button
                variant="outline"
                className="border-2 border-red-300 text-red-700 hover:bg-red-50 px-8 py-6 text-lg font-bold rounded-xl"
              >
                🎄 Als Gutschein verschenken
              </Button>
            </a>
          </div>
          <p className="mt-4 text-slate-600">
            Ideal als Geschenk für Eltern oder Großeltern!
          </p>
        </div>
      </div>
    </Section>
  );
};

const Times = () => {
  return (
    <Section id="zeiten" className="bg-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.05),transparent_70%)]"></div>
      
      <div className="grid items-center gap-16 lg:grid-cols-2 relative z-10">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 mb-6">
            <Clock size={16} />
            Verfügbarkeit
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Wichtiger Hinweis zu Arbeitszeiten & Vor-Ort-Terminen</h2>
          <p className="text-xl text-slate-600 leading-relaxed mb-8">
            Ich arbeite überwiegend remote und bin in der Regel Montag bis Freitag von 8:00–17:00 Uhr erreichbar. 
            Vor-Ort-Termine nach Absprache, primär in den Abendstunden.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:shadow-md hover:bg-white">
              <div className="p-2 rounded-full bg-[--brand-primary]/10">
                <Clock size={18} className="text-[--brand-primary]" />
              </div>
              <span className="text-slate-700 leading-relaxed font-medium">
                <strong>Montag, Dienstag, Donnerstag, Freitag:</strong> Vor-Ort-Termine abends möglich
              </span>
            </div>
            
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:shadow-md hover:bg-white">
              <div className="p-2 rounded-full bg-red-100">
                <Clock size={18} className="text-red-600" />
              </div>
              <span className="text-slate-700 leading-relaxed font-medium">
                <strong>Mittwoch:</strong> Keine Vor-Ort-Termine möglich
              </span>
            </div>
            
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:shadow-md hover:bg-white">
              <div className="p-2 rounded-full bg-orange-100">
                <Clock size={18} className="text-orange-600" />
              </div>
              <span className="text-slate-700 leading-relaxed font-medium">
                <strong>Samstag:</strong> In Ausnahmefällen nach vorheriger Vereinbarung auch samstags Vor-Ort-Termine möglich
              </span>
            </div>
            
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:shadow-md hover:bg-white">
              <div className="p-2 rounded-full bg-gray-100">
                <Clock size={18} className="text-gray-600" />
              </div>
              <span className="text-slate-700 leading-relaxed font-medium">
                <strong>Sonntag & Feiertage:</strong> Generell keine Vor-Ort-Termine
              </span>
            </div>
          </div>
        </div>
        
        <div className="relative">
          <div className="absolute -top-8 -right-8 w-64 h-64 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl"></div>
          
          <Card className="overflow-hidden border-0 shadow-2xl ring-1 ring-slate-200/60 rounded-3xl relative z-10 transform hover:scale-105 transition-transform duration-500">
            <CardContent className="p-0 relative">
              <div className="absolute top-6 right-6 z-10">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    Mo-Fr 8:00-17:00
                  </div>
                </div>
              </div>
              <img 
                src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6" 
                alt="Remote Support Arbeitsplatz" 
                className="h-96 w-full object-cover" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
};

const Prices = () => {
  return (
    <Section id="preise" className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-white relative">
      <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-blue-100/20 to-transparent"></div>
      
      <div className="relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Faire <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 bg-clip-text text-transparent">Preise</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Einfach, verständlich und ohne Fachchinesisch. Alle Preise sind Endpreise gemäß § 19 UStG (Kleinunternehmerregelung).
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Business Preise */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/60 hover:shadow-2xl transition-all duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                <Laptop2 size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Kleinunternehmen & Freiberufler</h3>
                <p className="text-slate-600">IT-Betreuung im Monatsabo</p>
              </div>
            </div>

            {/* Retainer-Pakete - MSP Focus */}
            <div className="mb-8">
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Shield size={18} className="text-green-600" />
                Retainer-Pakete (Monatsabo)
              </div>
              
              <p className="text-slate-600 mb-6 text-sm leading-relaxed bg-blue-50 p-4 rounded-xl border border-blue-100">
                Statt einzelne Einsätze zu bezahlen, sichern Sie sich mit unseren Retainer-Paketen eine laufende IT-Betreuung – planbar, kalkulierbar und mit klar definierten Reaktionszeiten.
              </p>
              
              <div className="space-y-4">
                {/* Retainer Basis */}
                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-[--brand-primary] hover:shadow-lg">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-1">Retainer Basis</div>
                      <p className="text-sm text-slate-600 mb-3 italic">
                        Für Solo-Selbstständige und kleine Büros, die ihre IT nicht sich selbst überlassen wollen.
                      </p>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1× monatlicher IT-Gesundheitscheck (Updates, Virenschutz, Backup-Kontrolle)</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>30 Minuten Remote-Support inklusive</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Reaktionszeit: innerhalb von 48 Stunden an Werktagen</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>10% Rabatt auf zusätzliche Leistungen</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-[--brand-primary] to-blue-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        ab 99 € / Monat
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        variant="outline"
                        size="sm"
                        className="text-sm border-[--brand-primary] text-[--brand-primary] hover:bg-[--brand-primary] hover:text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Retainer Standard - Empfohlen */}
                <div className="group relative rounded-2xl border-2 border-[--brand-primary] p-6 transition-all hover:shadow-xl bg-blue-50/30 shadow-lg">
                  <div className="absolute -top-3 left-6">
                    <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      ⭐ Empfohlen
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mt-2">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-1">Retainer Standard</div>
                      <p className="text-sm text-slate-600 mb-3 italic">
                        Für kleine Teams, die sich auf eine dauerhaft funktionierende IT verlassen müssen.
                      </p>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1× monatlicher IT-Gesundheitscheck</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>60 Minuten Remote-Support inklusive</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Reaktionszeit: innerhalb von 24 Stunden an Werktagen</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>15% Rabatt auf zusätzliche Leistungen</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-[--brand-primary] to-blue-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        ab 149 € / Monat
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        size="sm"
                        className="text-sm bg-[--brand-primary] hover:bg-[--brand-primary-dark] text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Retainer Premium */}
                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-[--brand-primary] hover:shadow-lg">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-1">Retainer Premium</div>
                      <p className="text-sm text-slate-600 mb-3 italic">
                        Für Betriebe, bei denen IT-Ausfälle richtig teuer werden.
                      </p>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1× monatlicher IT-Gesundheitscheck</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>120 Minuten Remote-Support inklusive</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1 Vor-Ort-Einsatz pro Quartal inklusive (bis 2 Stunden)</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Priorisierte Bearbeitung (Reaktion möglichst am selben Werktag)</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>20% Rabatt auf zusätzliche Leistungen</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-[--brand-primary] to-blue-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        ab 199 € / Monat
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        variant="outline"
                        size="sm"
                        className="text-sm border-[--brand-primary] text-[--brand-primary] hover:bg-[--brand-primary] hover:text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Einmal-Support ohne Vertrag */}
            <div className="mb-8">
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Zap size={18} className="text-orange-500" />
                Einmal-Support ohne Vertrag
              </div>
              
              {/* Desktop: Standard Table */}
              <div className="hidden md:block">
                <Table className="rounded-xl overflow-hidden border border-slate-200">
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">Leistung</TableHead>
                      <TableHead className="font-semibold">Beschreibung</TableHead>
                      <TableHead className="text-right font-semibold">Preis</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Remote-Support (Stundenbasis)</TableCell>
                      <TableCell className="text-slate-600">Schnelle Hilfe per Telefon, Video oder Fernwartung. Abrechnung je angefangene 15 Minuten, Mindestberechnung 30 Minuten.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">69,00 € / Std.</TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50/50">
                      <TableCell className="font-medium">Vor-Ort-Support (Stundenbasis)</TableCell>
                      <TableCell className="text-slate-600">Persönlicher IT-Support in Waren (Müritz) & Umgebung. Anfahrt inkl. Abrechnung je 15 Minuten, Mindestberechnung 60 Minuten.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">90,00 € / Std.</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">IT-Check für Kleinunternehmen</TableCell>
                      <TableCell className="text-slate-600">Bestandsaufnahme mit Schwachstellenanalyse & schriftlichem Bericht.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">103,50 € (einmalig)</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-slate-800 text-base">Remote-Support</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      69,00 € / Std.
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Schnelle Hilfe per Telefon, Video oder Fernwartung. Abrechnung je angefangene 15 Minuten, Mindestberechnung 30 Minuten.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-slate-800 text-base">Vor-Ort-Support</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      90,00 € / Std.
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Persönlicher IT-Support in Waren (Müritz) & Umgebung. Anfahrt inkl. Abrechnung je 15 Minuten, Mindestberechnung 60 Minuten.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-slate-800 text-base">IT-Check für Kleinunternehmen</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      103,50 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Bestandsaufnahme mit Schwachstellenanalyse & schriftlichem Bericht.
                  </p>
                </div>
              </div>
            </div>

            {/* Website-Services */}
            <div>
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Star size={18} className="text-purple-600" />
                Website-Services
              </div>
              {/* Desktop: Standard Table */}
              <div className="hidden md:block">
                <Table className="rounded-xl overflow-hidden border border-slate-200">
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">Leistung</TableHead>
                      <TableHead className="font-semibold">Beschreibung</TableHead>
                      <TableHead className="text-right font-semibold">Preis</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Website-Wartung WordPress</TableCell>
                      <TableCell className="text-slate-600">Monatliche Updates, Backups, Sicherheits-Scans.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">ab 48,00 € / Monat</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-slate-800 text-base">Website-Wartung WordPress</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      ab 48 € / Mon.
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Monatliche Updates, Backups, Sicherheits-Scans.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Senioren Preise */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/60 hover:shadow-2xl transition-all duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                <Users size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Senioren & Privatpersonen</h3>
                <p className="text-slate-600">Geduldige, verständliche Hilfe</p>
              </div>
            </div>

            {/* Sorglos-Pakete für Zuhause - NEU */}
            <div className="mb-8">
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Heart size={18} className="text-pink-500" />
                Sorglos-Pakete für Zuhause (Monatsabo)
              </div>
              
              <p className="text-slate-600 mb-6 text-sm leading-relaxed bg-green-50 p-4 rounded-xl border border-green-100">
                Für alle, die regelmäßig Unterstützung brauchen, aber nicht jedes Mal neu überlegen wollen, wen sie anrufen.
              </p>
              
              <div className="space-y-4">
                {/* Sorglos Zuhause Basic */}
                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-green-500 hover:shadow-lg">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-2">Sorglos Zuhause Basic</div>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1× Remote-Sicherheitscheck alle 6 Monate (Updates, Virenschutz, grundlegende Prüfung)</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>15 Minuten Telefonsupport pro Monat inklusive (z.B. für kurze Fragen)</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>10% Rabatt auf Vor-Ort-Termine</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        19 € / Monat
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        variant="outline"
                        size="sm"
                        className="text-sm border-green-500 text-green-600 hover:bg-green-500 hover:text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sorglos Zuhause Plus */}
                <div className="group relative rounded-2xl border-2 border-green-500 p-6 transition-all hover:shadow-xl bg-green-50/30 shadow-lg">
                  <div className="absolute -top-3 left-6">
                    <span className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      💚 Beliebte Wahl
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mt-2">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-2">Sorglos Zuhause Plus</div>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>1× Remote-Sicherheitscheck pro Quartal</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>30 Minuten Telefonsupport pro Monat inklusive</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Bevorzugte Terminvergabe bei Vor-Ort-Einsätzen</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600 text-sm">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>15% Rabatt auf Vor-Ort-Termine</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        39 € / Monat
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        size="sm"
                        className="text-sm bg-green-600 hover:bg-green-700 text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Flexible Hilfe */}
            <div className="mb-8">
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Clock size={18} className="text-[--brand-primary]" />
                Flexible Hilfe (Einzeltermine)
              </div>
              {/* Desktop: Standard Table */}
              <div className="hidden md:block">
                <Table className="rounded-xl overflow-hidden border border-slate-200">
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">Leistung</TableHead>
                      <TableHead className="font-semibold">Dauer</TableHead>
                      <TableHead className="text-right font-semibold">Preis</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Remote-Support Kurz-Hilfe</TableCell>
                      <TableCell className="text-slate-600">30 Min.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">30,00 €</TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50/50">
                      <TableCell className="font-medium">Remote-Support Standard-Sitzung</TableCell>
                      <TableCell className="text-slate-600">60 Min.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">50,00 €</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Remote-Support Verlängerung</TableCell>
                      <TableCell className="text-slate-600">je 15 Min.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">12,50 €</TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50/50">
                      <TableCell className="font-medium">Vor-Ort-Support erste Stunde</TableCell>
                      <TableCell className="text-slate-600">60 Min.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">70,00 €</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Vor-Ort-Support Verlängerung</TableCell>
                      <TableCell className="text-slate-600">je 15 Min.</TableCell>
                      <TableCell className="text-right font-bold text-[--brand-primary]">15,00 €</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-800 text-sm">Remote-Support Kurz-Hilfe</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      30,00 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs">30 Minuten</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-800 text-sm">Remote-Support Standard</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      50,00 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs">60 Minuten</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-800 text-sm">Remote Verlängerung</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      12,50 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs">je 15 Minuten</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-800 text-sm">Vor-Ort erste Stunde</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      70,00 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs">60 Minuten</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-bold text-slate-800 text-sm">Vor-Ort Verlängerung</h4>
                    <div className="bg-[--brand-primary]/10 text-[--brand-primary] px-3 py-1 rounded-lg font-bold text-sm">
                      15,00 €
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs">je 15 Minuten</p>
                </div>
              </div>
            </div>

            {/* Schulungen */}
            <div className="mb-8">
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Users size={18} className="text-green-600" />
                Schulungen
              </div>
              <div className="space-y-4">
                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-green-500 hover:shadow-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-2">Smartphone/Tablet-Schulung</div>
                      <div className="text-sm text-slate-600 mb-2">60 Minuten</div>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Remote: 50,00 €</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Vor-Ort: 70,00 €</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-green-500 hover:shadow-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-2">PC/Laptop-Grundlagen</div>
                      <div className="text-sm text-slate-600 mb-2">60 Minuten</div>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Remote: 50,00 €</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Vor-Ort: 70,00 €</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-green-500 hover:shadow-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-2">Online-Banking & Sicherheit</div>
                      <div className="text-sm text-slate-600 mb-2">60 Minuten</div>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Remote: 50,00 €</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Vor-Ort: 70,00 €</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Prepaid-Paket */}
            <div>
              <div className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Shield size={18} className="text-orange-600" />
                Prepaid-Paket
              </div>
              <div className="space-y-4">
                <div className="group relative rounded-2xl border-2 border-slate-200 p-6 transition-all hover:border-orange-500 hover:shadow-lg">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 text-lg mb-1">Remote-Support Paket S50 (Prepaid)</div>
                      <p className="text-sm text-slate-600 mb-3 italic">
                        5 × 30 Minuten Remote-Support mit Terminreservierung und Dokumentation
                      </p>
                      <ul className="space-y-1">
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Insgesamt 2,5 Stunden Remote-Support flexibel einlösbar</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Telefonischer Support während der Arbeitszeit</span>
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={16} className="mt-0.5 text-green-500 shrink-0" />
                          <span>Ersparnis gegenüber Einzelbuchungen</span>
                        </li>
                      </ul>
                    </div>
                    <div className="md:ml-6 flex flex-col items-end gap-2">
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                        140,00 €
                      </div>
                      <Button
                        onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                        variant="outline"
                        size="sm"
                        className="text-sm border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white"
                      >
                        Jetzt anfragen
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "Wie funktioniert Remote-Support?",
      answer: "Über eine sichere Verbindung kann ich auf Ihren Computer zugreifen und Probleme direkt lösen. Sie behalten dabei die volle Kontrolle und können die Verbindung jederzeit beenden."
    },
    {
      question: "Welche Zahlungsarten akzeptieren Sie?",
      answer: "Ich akzeptiere Barzahlung, Kartenzahlung, ApplePay, GooglePay, Überweisung und PayPal. Alle Preise sind Endpreise gemäß § 19 UStG (Kleinunternehmerregelung)."
    },
    {
      question: "Gibt es auch Notfall-Support?",
      answer: "Für Business-Kunden biete ich einen Notfall-Support an. Privatkunden können in dringenden Fällen auch außerhalb der regulären Zeiten anrufen."
    },
    {
      question: "Bieten Sie auch Hardware-Reparaturen an?",
      answer: "Ja, kleinere Hardware-Probleme löse ich vor Ort. Bei größeren Defekten arbeite ich mit vertrauenswürdigen lokalen Partnern zusammen."
    }
  ];

  return (
    <Section id="faq" className="bg-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.05),transparent_70%)]"></div>
      
      <div className="relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Häufige <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 bg-clip-text text-transparent">Fragen</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Antworten auf die wichtigsten Fragen zu unserem IT-Service
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-2xl border-2 border-slate-200 px-6 py-2 shadow-sm hover:shadow-md transition-all duration-300 hover:border-[--brand-primary]/30"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-[--brand-primary] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Section>
  );
};

// Gift Voucher Promotional Banner
const GiftVoucherBanner = () => (
  <Section id="gutschein-banner" className="bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 relative overflow-hidden">
    {/* Decorative elements */}
    <div className="absolute top-10 left-10 w-32 h-32 bg-red-200/30 rounded-full blur-2xl"></div>
    <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-200/30 rounded-full blur-2xl"></div>
    
    <div className="relative z-10 max-w-4xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-5 py-2 text-red-700 font-semibold mb-6">
        <Sparkles className="h-4 w-4" />
        Geschenkidee
      </div>
      
      <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
        <span className="bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">IT-Hilfe verschenken</span>
      </h2>
      
      <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
        Das perfekte Geschenk für Eltern oder Großeltern! Professionelle Unterstützung bei Computer, 
        Smartphone oder Tablet – <strong className="text-slate-800">geduldig erklärt, ohne Fachsprache</strong>.
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>Flexibler Betrag (10€ – 500€)</span>
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>Sofort per E-Mail</span>
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>12 Monate gültig</span>
        </div>
      </div>
      
      <Link to="/gutschein">
        <Button className="rounded-xl bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6 text-lg font-bold text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
          <Gift className="mr-2 h-5 w-5" />
          Gutschein kaufen
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>
      
      <p className="mt-6 text-sm text-slate-500 flex items-center justify-center gap-2">
        <Heart className="h-4 w-4 text-red-400" />
        Über 100 verschenkte Gutscheine – Freude garantiert!
      </p>
    </div>
  </Section>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", newsletter_opt_in: false });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast({ title: "Fehler", description: "Bitte füllen Sie alle Pflichtfelder aus.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await postLead(form);
      toast({ title: "Nachricht gesendet", description: "Vielen Dank für Ihre Anfrage. Wir melden uns bald bei Ihnen!" });
      setForm({ name: "", email: "", phone: "", company: "", message: "", newsletter_opt_in: false });
    } catch (error) {
      toast({ title: "Fehler", description: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <Section id="kontakt" className="bg-gradient-to-br from-slate-50 via-white to-slate-50 text-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-blue-100/30 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-full bg-gradient-to-l from-green-100/30 to-transparent"></div>
      </div>
      
      <div className="relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-slate-900">
            Jetzt <span className="bg-gradient-to-r from-[--brand-primary] to-blue-600 bg-clip-text text-transparent">Kontakt aufnehmen</span>
          </h2>
          <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
            Haben Sie Fragen oder benötigen Sie IT-Unterstützung? Wir helfen Ihnen gerne weiter!
          </p>
        </div>

        <div className="grid gap-16 lg:grid-cols-2 items-start">
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/60">
            <h3 className="text-2xl font-bold mb-8 text-slate-900">Ihre Nachricht an uns</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-base font-bold text-slate-900 mb-3">Name *</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-[--brand-primary] focus:ring-[--brand-primary] text-base px-4 py-3 font-medium"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                <div>
                  <label className="block text-base font-bold text-slate-900 mb-3">Telefon *</label>
                  <Input
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-[--brand-primary] focus:ring-[--brand-primary] text-base px-4 py-3 font-medium"
                    placeholder="Ihre Telefonnummer"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-base font-bold text-slate-900 mb-3">E-Mail *</label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-[--brand-primary] focus:ring-[--brand-primary] text-base px-4 py-3 font-medium"
                  placeholder="ihre@email.de"
                />
              </div>
              
              <div>
                <label className="block text-base font-bold text-slate-900 mb-3">
                  Firmenname (optional)
                  <span className="block text-sm text-slate-600 font-normal mt-1">
                    🏢 Für Kleinunternehmen & Freiberufler
                  </span>
                </label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-[--brand-primary] focus:ring-[--brand-primary] text-base px-4 py-3 font-medium"
                  placeholder="Name Ihres Unternehmens"
                />
              </div>
              

              
              <div>
                <label className="block text-base font-bold text-slate-900 mb-3">Nachricht *</label>
                <Textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-[--brand-primary] focus:ring-[--brand-primary] resize-none text-base px-4 py-3 font-medium"
                  placeholder="Beschreiben Sie Ihr IT-Problem oder Ihre Anfrage..."
                />
              </div>
              
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-start gap-4 mb-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    checked={form.newsletter_opt_in}
                    onChange={(e) => setForm({ ...form, newsletter_opt_in: e.target.checked })}
                    className="w-6 h-6 rounded text-[--brand-primary] bg-white border-2 border-slate-300 focus:ring-[--brand-primary] mt-1"
                  />
                  <label htmlFor="newsletter" className="text-base text-slate-900 font-bold leading-relaxed">
                    📧 Ja, ich möchte den IT-Newsletter mit hilfreichen Tipps erhalten
                  </label>
                </div>
                <p className="text-sm text-slate-700 ml-10 leading-relaxed">
                  {form.company && form.company.trim() ? (
                    "🏢 Sie erhalten Business-orientierte IT-Tipps für Kleinunternehmen"
                  ) : (
                    "👴 Sie erhalten verständliche IT-Tipps für Senioren & Privatpersonen"
                  )}
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[--brand-primary] to-blue-600 hover:from-blue-600 hover:to-[--brand-primary] text-white font-bold py-5 px-8 rounded-xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Mail size={24} className="mr-3" />
                    Nachricht senden
                  </>
                )}
              </Button>
            </form>
          </div>
          
          <div className="space-y-8">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/60">
              <h3 className="text-2xl font-bold mb-8 text-slate-900">Kontaktinformationen</h3>
              
              <div className="space-y-6">
                <a 
                  href="tel:+4915128958006"
                  className="flex items-start gap-4 p-6 bg-blue-50 rounded-2xl border-2 border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <div className="p-3 bg-[--brand-primary] rounded-xl">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 mb-2 text-lg">Telefon</div>
                    <div className="text-[--brand-primary] font-bold text-xl">01512 895 800 6</div>
                    <div className="text-slate-700 mt-1 font-medium text-base">Mo-Fr 8:00-18:00 Uhr</div>
                  </div>
                </a>
                
                <a 
                  href="https://wa.me/4915128958006?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zum%20IT-Support."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-green-50 rounded-2xl border-2 border-green-200 hover:bg-green-100 transition-colors"
                >
                  <div className="p-3 bg-green-600 rounded-xl">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 mb-2 text-lg">WhatsApp</div>
                    <div className="text-green-700 font-bold text-xl">Direkt schreiben</div>
                    <div className="text-slate-700 mt-1 font-medium text-base">Schnelle Antwort garantiert</div>
                  </div>
                </a>
                
                <a 
                  href="mailto:kontakt@klartech-mueritz.de"
                  className="flex items-start gap-4 p-6 bg-purple-50 rounded-2xl border-2 border-purple-200 hover:bg-purple-100 transition-colors"
                >
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 mb-2 text-lg">E-Mail</div>
                    <div className="text-purple-700 font-bold text-lg">kontakt@klartech-mueritz.de</div>
                    <div className="text-slate-700 mt-1 font-medium text-base">Antwort innerhalb von 24h</div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Google Maps */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-200/60">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                  <MapPin className="h-6 w-6 text-[--brand-primary]" />
                  Standort: Waren (Müritz)
                </h3>
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d37956.02069!2d12.65!3d53.52!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47ab0a0b4b36e0f5%3A0x4250d7a0f29f1b0!2s17192%20Waren%20(M%C3%BCritz)!5e0!3m2!1sde!2sde!4v1700000000000!5m2!1sde!2sde"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort Klartech Müritz - Waren (Müritz)"
                className="grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
            
            <div className="bg-gradient-to-br from-green-100 to-blue-100 rounded-2xl border-2 border-green-300 p-8 shadow-xl">
              <div className="flex items-center gap-4 mb-4">
                <CheckCircle size={32} className="text-green-600" />
                <span className="font-bold text-slate-900 text-xl">Kostenloses Erstgespräch</span>
              </div>
              <p className="text-slate-800 text-lg leading-relaxed font-medium">
                Rufen Sie uns an oder schreiben Sie uns. Wir beraten Sie gerne kostenlos und unverbindlich!
              </p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Footer = () => {
  const nav = [
    { label: "Warum", href: "#warum" },
    { label: "Leistungen", href: "#leistungen" },
    { label: "Zeiten", href: "#zeiten" },
    { label: "Preise", href: "#preise" },
    { label: "FAQ", href: "#faq" },
    { label: "Blog", href: "/blog", internal: true },
    { label: "Klartech Kompakt", href: "https://kompakt.klartech-mueritz.de", external: true },
  ];

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer className="bg-slate-900 text-white py-12 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20"></div>
      
      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white rounded-lg p-3 shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png" 
                  alt="Klartech Müritz Logo" 
                  className="h-8 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed mb-6 max-w-md">
              Ihr vertrauensvoller IT-Partner in Waren (Müritz). Wir bieten geduldige, 
              verständliche IT-Unterstützung für Senioren, Privatleute und kleine Unternehmen.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                Online verfügbar
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-bold mb-4 text-white">Navigation</h3>
            <ul className="space-y-3">
              {nav.map((item, index) => (
                <li key={item.href + '-footer-' + index}>
                  {item.external ? (
                    <a
                      href={item.href}
                      className="text-slate-300 hover:text-white transition-colors text-sm"
                    >
                      {item.label}
                    </a>
                  ) : item.internal ? (
                    <Link
                      to={item.href}
                      className="text-slate-300 hover:text-white transition-colors text-sm"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollTo(item.href)}
                      className="text-slate-300 hover:text-white transition-colors text-sm"
                    >
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold mb-4 text-white">Rechtliches</h3>
            <ul className="space-y-3">
              <li>
                <a href="/impressum" className="text-slate-300 hover:text-white transition-colors text-sm">
                  Impressum
                </a>
              </li>
              <li>
                <a href="/datenschutz" className="text-slate-300 hover:text-white transition-colors text-sm">
                  Datenschutz
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollTo("#kontakt")}
                  className="text-slate-300 hover:text-white transition-colors text-sm"
                >
                  Kontakt
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © 2024 Klartech Müritz. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6">
              <a href="tel:+4915128958006" className="text-slate-400 hover:text-white transition-colors text-sm">
                +49 (0) 1512 895 800 6
              </a>
              <a href="mailto:kontakt@klartech-mueritz.de" className="text-slate-400 hover:text-white transition-colors text-sm">
                kontakt@klartech-mueritz.de
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function LandingComplete() {
  // SEO für Hauptseite
  useSEO(SEO_PRESETS.home);

  return (
    <div className="min-h-screen">
      <SeasonalDecorations />
      <Header />
      <main>
        <Hero />
        <ChristmasDeviceSetup />
        <Why />
        <Services />
        <Times />
        <Prices />
        <FAQ />
        <GiftVoucherBanner />
        <LatestBlogPosts />
        <Contact />
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}